<div class="row g-0 permissions-layout">
  <!-- ─── Designations ──────────────────────────────────────────────────────── -->
  <div class="col-md-4 col-lg-3">
    <div class="panel designation-panel h-100">
      <div class="panel-head">
        <div class="d-flex align-items-center justify-content-between">
          <h6 class="panel-title mb-0">Designations</h6>
          <span class="app-badge badge-secondary">{{ designations.length }}</span>
        </div>
        <div class="input-group mt-2">
          <span class="input-group-text"><i class="bi bi-search"></i></span>
          <input
            type="text"
            class="form-control"
            placeholder="Search designations"
            [(ngModel)]="designationSearch"
            (ngModelChange)="filterDesignations()" />
        </div>
      </div>

      <div class="designation-list">
        @if (isLoadingDesignations) {
          <app-loading-spinner [fullHeight]="false"></app-loading-spinner>
        } @else if (filteredDesignations.length === 0) {
          <p class="text-muted small text-center py-4 mb-0">No designations match "{{ designationSearch }}".</p>
        } @else {
          @for (item of filteredDesignations; track item.id) {
            <button
              type="button"
              class="designation-item"
              [class.is-active]="selectedDesignationId === item.id"
              (click)="onSelectDesignation(item.id)">
              <i class="bi bi-person-badge"></i>
              <span class="text-truncate">{{ item.title }}</span>
              @if (selectedDesignationId === item.id && dirtyCount > 0) {
                <span class="dirty-dot" title="Unsaved changes"></span>
              }
            </button>
          }
        }
      </div>
    </div>
  </div>

  <!-- ─── Permission matrix ─────────────────────────────────────────────────── -->
  <div class="col-md-8 col-lg-9">
    <div class="panel matrix-panel h-100">
      <!-- Header: who you are editing, and the save controls -->
      <div class="panel-head d-flex flex-wrap justify-content-between align-items-center gap-3">
        <div>
          <h6 class="panel-title mb-1">
            Permissions for
            <span class="text-primary">{{ selectedDesignationTitle }}</span>
          </h6>
          <span class="text-muted small">
            <strong>{{ selectedPermissionIds.size }}</strong>
            of {{ totalPermissionCount }} permissions enabled
            @if (dirtyCount > 0) {
              <span class="app-badge badge-soft-warning ms-2">{{ dirtyCount }} unsaved</span>
            }
          </span>
        </div>

        <div class="d-flex align-items-center gap-2">
          <button class="btn btn-secondary btn-sm" [disabled]="dirtyCount === 0 || isSaving" (click)="resetChanges()">
            <i class="bi bi-arrow-counterclockwise"></i>
            Reset
          </button>
          <button
            class="btn btn-primary btn-sm px-3"
            [disabled]="isSaving || !selectedDesignationId || dirtyCount === 0"
            (click)="savePermissions()">
            @if (isSaving) {
              <app-loading-spinner [inline]="true"></app-loading-spinner>
            } @else {
              <i class="bi bi-check2"></i>
            }
            Save Changes
          </button>
        </div>
      </div>

      @if (isLoadingTree || isLoadingMatrix) {
        <app-loading-spinner></app-loading-spinner>
      } @else {
        <!-- Toolbar: search, bulk actions, copy -->
        <div class="panel-toolbar">
          <div class="input-group toolbar-search">
            <span class="input-group-text"><i class="bi bi-search"></i></span>
            <input
              type="text"
              class="form-control"
              placeholder="Filter modules, screens or actions"
              [(ngModel)]="permissionSearch"
              (ngModelChange)="filterPermissions()" />
            @if (permissionSearch) {
              <button class="btn btn-secondary" type="button" (click)="clearPermissionSearch()">
                <i class="bi bi-x-lg"></i>
              </button>
            }
          </div>

          <div class="toolbar-actions">
            <select class="form-select form-select-sm copy-select" [(ngModel)]="copySourceId">
              <option value="" disabled>Copy from designation</option>
              @for (option of copySourceOptions; track option.id) {
                <option [value]="option.id">{{ option.title }}</option>
              }
            </select>
            <button
              class="btn btn-soft-primary btn-sm"
              [disabled]="!copySourceId || isCopying"
              (click)="copyFromDesignation()">
              @if (isCopying) {
                <app-loading-spinner [inline]="true"></app-loading-spinner>
              } @else {
                <i class="bi bi-files"></i>
              }
              Copy
            </button>

            <span class="toolbar-divider"></span>

            <button class="btn btn-link btn-sm" (click)="selectAll()">Select all</button>
            <button class="btn btn-link btn-sm" (click)="clearAll()">Clear all</button>
            <button class="btn btn-link btn-sm" (click)="expandAll()">Expand</button>
            <button class="btn btn-link btn-sm" (click)="collapseAll()">Collapse</button>
          </div>
        </div>

        @if (!isAdminAccessGranted) {
          <div class="lock-alert">
            <app-info-alert>
              <i class="bi bi-lock me-1"></i>
              <strong>{{ selectedDesignationTitle }}</strong>
              has no admin site login, so the module permissions below are disabled. Grant
              <strong>Admin Site</strong>
              under login access to edit them.
            </app-info-alert>
          </div>
        }

        <div class="matrix-scroll">
          @if (filteredTree.length === 0) {
            <div class="empty-state-container">
              <i class="bi bi-search"></i>
              <h5>No permissions found</h5>
              <p>Nothing matches "{{ permissionSearch }}". Clear the filter to see all modules.</p>
            </div>
          }

          @for (module of filteredTree; track module.name) {
            <section class="module" [class.is-locked]="isModuleLocked(module.name)">
              <!-- Module header -->
              <div
                class="module-head"
                role="button"
                tabindex="0"
                (click)="toggleModule(module.name)"
                (keydown.enter)="toggleModule(module.name)"
                (keydown.space)="toggleModule(module.name)">
                <i
                  class="bi module-chevron"
                  [class.bi-chevron-down]="isModuleExpanded(module.name)"
                  [class.bi-chevron-right]="!isModuleExpanded(module.name)"></i>

                <span class="module-name">{{ module.label || module.name }}</span>

                <span class="app-badge badge-soft-primary">
                  {{ getModuleSelectedCount(module) }} / {{ getModuleTotalCount(module) }}
                </span>
              </div>

              <!-- Module matrix -->
              @if (isModuleExpanded(module.name)) {
                <div class="module-body">
                  <table class="permission-table">
                    <thead>
                      <tr>
                        <th class="resource-col">Screen / Feature</th>
                        @for (action of getModuleActions(module.name); track action) {
                          <th class="action-col">
                            @if (showBulkSelectors(module.name)) {
                              <label class="action-head">
                                <input
                                  type="checkbox"
                                  class="form-check-input"
                                  [checked]="isColumnFullyChecked(module, action)"
                                  [indeterminate]="isColumnPartiallyChecked(module, action)"
                                  [disabled]="isModuleLocked(module.name)"
                                  (change)="toggleColumn(module, action)" />
                                <span>{{ getActionLabel(action) | titlecase }}</span>
                              </label>
                            } @else {
                              <span>{{ getActionLabel(action) | titlecase }}</span>
                            }
                          </th>
                        }
                      </tr>
                    </thead>
                    <tbody>
                      @for (resource of module.resources; track resource.name) {
                        <tr>
                          <td class="resource-col">
                            <label class="resource-label" [class.is-static]="!showBulkSelectors(module.name)">
                              @if (showBulkSelectors(module.name)) {
                                <input
                                  type="checkbox"
                                  class="form-check-input"
                                  [checked]="isResourceFullyChecked(resource)"
                                  [indeterminate]="isResourcePartiallyChecked(resource)"
                                  [disabled]="isModuleLocked(module.name)"
                                  (change)="toggleResource(resource)" />
                              }
                              <span>
                                <span class="resource-name">
                                  {{ getResourceDisplayLabel(module, resource) }}
                                </span>
                                <span class="resource-code">{{ module.name }}.{{ resource.name }}</span>
                              </span>
                            </label>
                          </td>

                          @for (action of getModuleActions(module.name); track action) {
                            <td class="action-col">
                              @if (getPermissionId(resource, action); as permissionId) {
                                <input
                                  type="checkbox"
                                  class="form-check-input permission-box"
                                  [id]="'perm-' + permissionId"
                                  [checked]="isPermissionChecked(permissionId)"
                                  [disabled]="isModuleLocked(module.name)"
                                  (change)="togglePermission(permissionId)" />
                              } @else {
                                <span class="not-applicable" title="Not applicable">–</span>
                              }
                            </td>
                          }
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              }
            </section>
          }
        </div>
      }
    </div>
  </div>
</div>
